import apiInstance from './api';

/**
 * @typedef {'asc' | 'desc'} SortOrder
 */

/**
 * @typedef {'dia' | 'tia' | 'tematik'} EncyclopediaType
 */

/**
 * @typedef {'issue' | 'no_issue' | 'in_review'} IssueStatus - Sorun Var | Sorun Yok | Inceleniyor
 */

/**
 * @typedef {Object} RelationOverviewParams
 * @property {number} [page=1] - Sayfa numarasi
 * @property {number} [itemsPerPage=20] - Sayfa basina gosterilecek oge sayisi
 * @property {boolean} [pagination] - Sayfalama aktif/pasif
 * @property {string} [title] - Basliga gore filtre
 * @property {EncyclopediaType} [type] - Ansiklopedi tipine gore tekil filtre
 * @property {EncyclopediaType[]} [type[]] - Ansiklopedi tipine gore coklu filtre
 * @property {number} [relationsCount] - Iliski sayisina gore tekil filtre
 * @property {number[]} [relationsCount[]] - Iliski sayisina gore coklu filtre
 * @property {boolean} [hasNoRelation] - Iliskisi olmayan maddeleri filtrele
 * @property {boolean} [hasChecked] - Ic kontrol durumuna gore filtrele
 * @property {SortOrder} [order[title]='asc'] - Basliga gore siralama
 * @property {SortOrder} [order[type]='asc'] - Ansiklopedi tipine gore siralama
 * @property {SortOrder} [order[relationsCount]='asc'] - Iliski sayisina gore siralama
 * @property {SortOrder} [order[hasNoRelation]='asc'] - Iliskisi olmayanlar sirasi
 * @property {SortOrder} [order[hasChecked]='asc'] - Ic kontrol durumuna gore siralama
 */

/**
 * @typedef {Object} RelationOverviewItem
 * @property {string} uid - Benzersiz tanimlayici
 * @property {string} title - Madde basligi
 * @property {EncyclopediaType} type - Ansiklopedi tipi (dia, tia, tematik)
 * @property {number} relationsCount - Iliskili madde sayisi
 * @property {boolean} hasNoRelation - Iliskisi olup olmadigini belirtir
 * @property {boolean} hasChecked - Ic kontrol durumu
 * @property {string|null} articleGroup - Madde grubu
 * @property {string|null} lead - Takdim cumlesi
 * @property {string|null} kunya - Kunye
 * @property {string|null} lifespan - Dogum olum tarihi
 * @property {boolean|null} isAbandoned - Terk mi
 * @property {boolean|null} isDraft - Taslak mi
 * @property {boolean|null} isCancelled - Iptal mi
 * @property {string|null} articleType - Madde turu
 * @property {string|null} committee - Sorumlu ilim heyeti
 */

/**
 * @typedef {Object} RelationOverviewDetailParams
 * @property {string} uid - Relation group benzersiz tanimlayicisi
 */

/**
 * @typedef {Object} UnifiedRelationsParams
 * @property {string} baseType - Ana maddenin tipi
 * @property {string} baseId - Ana maddenin ID'si
 * @property {number} [page=1] - Sayfa numarasi
 * @property {number} [itemsPerPage=20] - Sayfa basina gosterilecek oge sayisi
 * @property {boolean} [pagination] - Sayfalama aktif/pasif
 * @property {string} [relatedTitle] - Madde adina gore filtre
 * @property {'direct' | 'indirect'} [direction] - Iliski yonune gore tekil filtre
 * @property {('direct' | 'indirect')[]} [direction[]] - Iliski yonune gore coklu filtre
 * @property {EncyclopediaType} [relatedType] - Ansiklopedi tipine gore tekil filtre
 * @property {EncyclopediaType[]} [relatedType[]] - Ansiklopedi tipine gore coklu filtre
 * @property {IssueStatus} [hasIssue] - Sorun durumuna gore filtre
 * @property {SortOrder} [order[relatedTitle]] - Madde adina gore siralama
 * @property {SortOrder} [order[direction]] - Iliski yonune gore siralama
 * @property {SortOrder} [order[relationsCount]] - Iliskili madde sayisina gore siralama
 * @property {SortOrder} [order[relatedType]] - Ansiklopedi tipine gore siralama
 * @property {SortOrder} [order[createdAt]] - Olusturulma tarihine gore siralama
 * @property {SortOrder} [order[sortOrder]] - Sira numarasina gore siralama
 */

/**
 * @typedef {Object} RelationGroupBody
 * @property {string} title - Grup basligi
 */

/**
 * @typedef {Object} RelationItemBody
 * @property {string} relationGroup - Iliski grubunun IRI'si
 * @property {string} article - Maddenin IRI'si
 */

/**
 * @typedef {Object} UpsertStatusBody
 * @property {Object} body - Durum bilgisi
 */

/**
 * @typedef {Object} UpdateNoteParams
 * @property {string} id - Relation item ID'si
 * @property {Object} body - Not icerigi
 */

/**
 * @typedef {Object} UpdateIssueParams
 * @property {string} id - Relation item ID'si
 * @property {{ hasIssue: IssueStatus }} body - Sorun durumu enum'u
 */

/**
 * @typedef {Object} GlobalSearchParams
 * @property {string} q - Arama metni
 * @property {boolean} [excludeGroupSources] - true gonderilirse, zaten bir RelationGroup'un source'u olan maddeler sonuclardan cikarilir.
 * @property {number} [excludeTargetsOfGroup] - Belirtilen groupId'ye ait mevcut target'lar sonuclardan cikarilir.
 * @property {string} [excludeId] - {type}-{id} formatinda tek bir maddeyi cikarir. Gecerli type degerleri: tematik, dia, tia (orn: tematik-123)
 */

export const articleRelationManagementApi = apiInstance.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Birlesik iliskileri getirir.
     * GET /encyclopedia/unified_relations/{baseType}/{baseId}
     * @param {UnifiedRelationsParams} params
     */
    getUnifiedRelations: build.query({
      query: ({ baseType, baseId, ...params }) => ({
        url: `/encyclopedia/unified_relations/${baseType}/${baseId}`,
        params,
      }),
      providesTags: ['ArticleUnifiedRelations'],
    }),
    /**
     * Global arama ile madde arar.
     * GET /encyclopedia/global_searches
     * @param {GlobalSearchParams} params
     */
    searchArticlesForRelationManagement: build.query({
      query: ({ q, excludeGroupSources, excludeTargetsOfGroup, excludeId, ...rest }) => ({
        url: '/encyclopedia/global_searches',
        params: { q, excludeGroupSources, excludeTargetsOfGroup, excludeId, ...rest },
      }),
      transformResponse: (response) => response['hydra:member'],
      providesTags: ['ArticleRelationManagement'],
    }),
    /**
     * Sonsuz kaydirmali (infinite scroll) madde aramasi.
     * GET /encyclopedia/global_searches
     * @param {GlobalSearchParams} queryArg
     */
    searchArticlesInfinite: build.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages, lastPageParam) =>
          lastPage['hydra:member'].length > 0 ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        const { q, excludeGroupSources, excludeTargetsOfGroup, excludeId } = queryArg;
        return {
          url: '/encyclopedia/global_searches',
          params: { q, excludeGroupSources, excludeTargetsOfGroup, excludeId, page: pageParam, itemsPerPage: 20 },
        };
      },
      providesTags: ['ArticleRelationManagement'],
    }),
    /**
     * Sonsuz kaydirmali birlesik iliskileri getirir.
     * GET /encyclopedia/unified_relations/{baseType}/{baseId}
     * @param {{ baseType: string, baseId: string|number, direction: string }} queryArg
     */
    getUnifiedRelationsInfinite: build.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages, lastPageParam) =>
          lastPage['hydra:member'].length > 0 ? lastPageParam + 1 : undefined,
      },
      query({ queryArg, pageParam }) {
        const { baseType, baseId, direction } = queryArg;
        return {
          url: `/encyclopedia/unified_relations/${baseType}/${baseId}`,
          params: { direction, page: pageParam, itemsPerPage: 20 },
        };
      },
      providesTags: ['ArticleUnifiedRelations'],
    }),
    /**
     * Iliski grubu genel gorunumlerini listeler.
     * GET /encyclopedia/relation_overviews
     * @param {RelationOverviewParams} params - Filtreleme, siralama ve sayfalama parametreleri
     * @returns {RelationOverviewItem[]} Iliski grubu listesi
     */
    getRelationGroupOverview: build.query({
      query: (params) => ({
        url: '/encyclopedia/relation_overviews',
        params,
      }),
      providesTags: ['ArticleRelationOverview'],
    }),
    /**
     * Belirli bir iliski grubunun detayini getirir.
     * GET /encyclopedia/relation_overviews/{uid}
     * @param {RelationOverviewDetailParams} params
     */
    getRelationGroupOverviewDetail: build.query({
      query: ({ uid }) => `/encyclopedia/relation_overviews/${uid}`,
      providesTags: ['ArticleRelationOverviewDetail'],
    }),
    /**
     * Yeni bir iliski grubu olusturur.
     * POST /encyclopedia/relation_groups
     * @param {RelationGroupBody} body
     */
    createArticleRelationGroup: build.mutation({
      query: (body) => ({
        url: '/encyclopedia/relation_groups',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ArticleRelationManagement', 'ArticleRelationOverviewDetail', 'ArticleRelationOverview', 'ArticleUnifiedRelations'],
    }),
    /**
     * Iliski grubuna yeni ogeler ekler.
     * POST /encyclopedia/relation_items
     * @param {RelationItemBody} body
     */
    createRelationItems: build.mutation({
      query: (body) => ({
        url: '/encyclopedia/relation_items',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ArticleRelationManagement', 'ArticleRelationOverviewDetail', 'ArticleRelationOverview', 'ArticleUnifiedRelations'],
    }),
    /**
     * Yeni bir madde iliskisi olusturur.
     * POST /encyclopedia/article_relations
     */
    createArticleRelationManagement: build.mutation({
      query: (body) => ({
        url: '/encyclopedia/article_relations',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ArticleRelationManagement', 'ArticleRelationOverviewDetail', 'ArticleRelationOverview', 'ArticleUnifiedRelations'],
    }),
    /**
     * Iliski grubunun durumunu gunceller veya olusturur.
     * POST /encyclopedia/relation_groups/upsert_status
     * @param {UpsertStatusBody} params
     */
    upsertStatus: build.mutation({
      query: ({ body }) => ({
        url: `/encyclopedia/relation_groups/upsert_status`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ArticleRelationManagement', 'ArticleRelationOverviewDetail', 'ArticleRelationOverview', 'ArticleUnifiedRelations'],
    }),
    /**
     * Bir iliski ogesinin notunu gunceller.
     * PUT /encyclopedia/relation_items/{id}/note
     * @param {UpdateNoteParams} params
     */
    updateArticleRelationNote: build.mutation({
      query: ({ id, body }) => ({
        url: `/encyclopedia/relation_items/${id}/note`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['ArticleRelationManagement', 'ArticleRelationOverviewDetail', 'ArticleRelationOverview', 'ArticleUnifiedRelations'],
    }),
    /**
     * Bir iliski ogesinin sorun durumunu gunceller.
     * PUT /encyclopedia/relation_items/{id}/has_issue
     *
     * Durum tablo hucresinden tek tikla degistirildigi icin guncelleme optimistic yapilir:
     * cache aninda yamalanir, istek basarisiz olursa yama geri alinir.
     *
     * hasIssue ayni zamanda bir sunucu filtresi oldugu icin yama tek basina yetmez:
     * filtre aktifken satirin listeye ait olup olmadigi da degisir, bunu client
     * hesaplayamaz (hydra:totalItems ve sayfalama da kayar). Bu yuzden yalnizca
     * hasIssue filtresi aktif olan bir cache girdisi varsa tag invalidate edilir.
     * Filtresiz gorunumde satir her durumda listede kaldigi icin yama yeterlidir
     * ve gereksiz refetch yapilmaz.
     * @param {UpdateIssueParams} params
     */
    updateArticleRelationIssue: build.mutation({
      query: ({ id, body }) => ({
        url: `/encyclopedia/relation_items/${id}/has_issue`,
        method: 'PUT',
        body,
      }),
      async onQueryStarted({ id, body }, { dispatch, getState, queryFulfilled }) {
        // Farkli sayfa/filtre kombinasyonlari ayri cache girdileri olusturdugu icin hepsi yamalanir
        const entries = apiInstance.util
          .selectInvalidatedBy(getState(), ['ArticleUnifiedRelations'])
          .filter(({ endpointName }) => endpointName === 'getUnifiedRelations');

        // Sorun durumuna gore filtrelenmis bir liste varsa satirin o listeye ait olup
        // olmadigi degisebilecegi icin yamanin ustune yeniden cekim gerekir
        const hasFilteredEntry = entries.some(({ originalArgs }) => originalArgs?.hasIssue !== undefined);

        const patches = entries.map(({ originalArgs }) =>
          dispatch(
            apiInstance.util.updateQueryData('getUnifiedRelations', originalArgs, (draft) => {
              const item = draft['hydra:member']?.find((relation) => relation.itemId === id);
              if (item) item.hasIssue = body.hasIssue;
            }),
          ),
        );

        try {
          await queryFulfilled;
          if (hasFilteredEntry) {
            dispatch(apiInstance.util.invalidateTags(['ArticleUnifiedRelations']));
          }
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),
    /**
     * Bir iliski ogesini siler.
     * DELETE /encyclopedia/relation_items/{id}
     * @param {{ id: string }} params
     */
    deleteRelationItem: build.mutation({
      query: ({ id }) => ({
        url: `/encyclopedia/relation_items/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ArticleRelationManagement', 'ArticleRelationOverviewDetail', 'ArticleRelationOverview', 'ArticleUnifiedRelations'],
    }),
    /**
     * Bir iliski grubunu siler.
     * DELETE /encyclopedia/relation_overviews/{uid}
     * @param {{ uid: string }} params
     */
    deleteRelationGroup: build.mutation({
      query: ({ uid }) => ({
        url: `/encyclopedia/relation_overviews/${uid}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ArticleRelationManagement', 'ArticleRelationOverviewDetail', 'ArticleRelationOverview', 'ArticleUnifiedRelations'],
    }),
  }),
  overrideExisting: true,
});
